import { ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  createMultiStyleConfigHelpers,
  Divider,
  Flex,
  SystemStyleObject,
  useMultiStyleConfig
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';

import Icon, { IconName } from '@/components/atomic/v1/icon/Icon';

const parts = ['mainButton', 'splitButton', 'divider'] as const;

type Parts = (typeof parts)[number];

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts);

type SplitButtonColor = 'grey' | 'blue' | 'orange';

const activeFocusOutlineStyle: SystemStyleObject = {
  outline: '2px solid',
  outlineColor: 'button-secondary/blue:active',
  borderRadius: 4,

  // ensure the outline is visible above adjacent elements
  position: 'relative',
  zIndex: 1
};

// eslint-disable-next-line consistent-return
function splitButtonColorStyles(color: SplitButtonColor): Record<Parts, SystemStyleObject> {
  const greyButtonStyles: SystemStyleObject = {
    bg: 'grey.20',
    color: 'button-primary/blue:default',
    _hover: { bg: 'background/secondary', color: 'button-secondary/blue:hover' },
    _active: {
      bg: 'background/secondary',
      color: 'button-secondary/blue:active',
      ...activeFocusOutlineStyle
    },
    _focus: {
      bg: 'background/secondary',
      color: 'button-secondary/blue:active',
      ...activeFocusOutlineStyle
    }
  };
  const blueButtonStyles: SystemStyleObject = {
    bg: 'button-secondary/blue:active',
    color: 'white',
    _hover: { bg: 'background/secondary', color: 'button-secondary/blue:hover' },
    _active: {
      bg: 'background/secondary',
      color: 'button-secondary/blue:active',
      ...activeFocusOutlineStyle
    },
    _focus: { bg: 'background/secondary', color: 'button-secondary/blue:active', ...activeFocusOutlineStyle }
  };
  const orangeButtonStyles: SystemStyleObject = {
    bg: 'button-primary/orange:default',
    color: 'white',
    _hover: { bg: 'button-secondary/orange/bg:hover', color: 'button-secondary/orange:hover' },
    _active: {
      bg: 'button-secondary/orange/bg:hover',
      color: 'button-secondary/orange:hover',
      ...activeFocusOutlineStyle
    },
    _focus: {
      bg: 'button-secondary/orange/bg:hover',
      color: 'button-secondary/orange:hover',
      ...activeFocusOutlineStyle
    }
  };

  // eslint-disable-next-line default-case
  switch (color) {
    case 'grey':
      return {
        mainButton: greyButtonStyles,
        splitButton: greyButtonStyles,
        divider: { borderColor: 'icon/default' }
      };
    case 'blue':
      return {
        mainButton: blueButtonStyles,
        splitButton: blueButtonStyles,
        divider: { borderColor: 'white' }
      };
    case 'orange':
      return {
        mainButton: orangeButtonStyles,
        splitButton: orangeButtonStyles,
        divider: { borderColor: 'white' }
      };
  }
}

const baseStyle = definePartsStyle((props) => {
  const colorStyles = splitButtonColorStyles(props.color as SplitButtonColor);

  return {
    mainButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      h: 12,
      px: 3,
      fontWeight: 'bold',
      ...colorStyles.mainButton,
      _disabled: {
        bg: 'status/disabled/bg',
        color: 'button-primary/disabled',
        cursor: 'not-allowed'
      }
    },
    splitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      w: 12,
      h: 12,
      ...colorStyles.splitButton,
      _disabled: {
        bg: 'status/disabled/bg',
        color: 'button-primary/disabled',
        cursor: 'not-allowed'
      }
    },
    divider: {
      opacity: 1,
      ...colorStyles.divider
    }
  };
});

export const splitButtonTheme = defineMultiStyleConfig({
  baseStyle
});

type SplitButtonProps = {
  children?: ReactNode;
  mainButtonProps?: ButtonProps & {
    leftIconName?: IconName;
    rightIconName?: IconName;
  };
  splitButtonProps?:
    | (ButtonProps & {
        iconName?: IconName;
      })
    | undefined;
  color: SplitButtonColor;
};

const SplitButton = ({ children, mainButtonProps = {}, splitButtonProps, color }: SplitButtonProps) => {
  const styles = useMultiStyleConfig('SplitButton', { color });

  const hasMainButton = Boolean(children);
  const hasSplitButton = splitButtonProps && !isEmpty(splitButtonProps);

  const { leftIconName, rightIconName, ...restMainButtonProps } = mainButtonProps;
  const { iconName: splitIconName, ...restSplitButtonProps } = splitButtonProps ?? {};

  const mainButtonDisabled = Boolean(
    restMainButtonProps.isDisabled || restMainButtonProps.disabled || restMainButtonProps.isLoading
  );
  const splitButtonDisabled = Boolean(
    restSplitButtonProps?.isDisabled || restSplitButtonProps?.disabled || restSplitButtonProps?.isLoading
  );

  const mainButtonCss: SystemStyleObject = {
    ...styles.mainButton,
    ...(hasSplitButton ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {})
  };

  const splitButtonCss: SystemStyleObject = {
    ...styles.splitButton,
    ...(hasMainButton ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {})
  };

  const dividerCss: SystemStyleObject = {
    ...styles.divider,
    ...(mainButtonDisabled && splitButtonDisabled
      ? { borderColor: 'button-primary/disabled', cursor: 'not-allowed' }
      : {})
  };

  return (
    <Flex gap={0} wrap="nowrap" h={12}>
      {hasMainButton && (
        <Button __css={mainButtonCss} {...restMainButtonProps}>
          {leftIconName && <Icon name={leftIconName} size={18} mr={hasSplitButton ? 3 : 1} />}
          {children}
          {rightIconName && <Icon name={rightIconName} size={18} ml={hasSplitButton ? 3 : 1} />}
        </Button>
      )}

      {hasMainButton && hasSplitButton && <Divider __css={dividerCss} orientation="vertical" />}

      {hasSplitButton && (
        <Button __css={splitButtonCss} {...restSplitButtonProps}>
          <Icon name={splitIconName ?? 'arrow-down'} size={18} />
        </Button>
      )}
    </Flex>
  );
};

export default SplitButton;
